import * as React from 'react';
import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

import useBoundingBox from '@/hooks/use-bounding-box';

import Spacer from '@/components/Spacer';
import Spinner from '@/components/Spinner';
import Demo, {
  DemoToggleButtonGroupControl,
} from '@/components/Demo';

const MAX_WIDTH = 440;
const DEEP_PURPLE_VALS = '240deg 40% 20%';
const DEEP_PURPLE = `hsl(${DEEP_PURPLE_VALS})`;
const LIGHT_PURPLE_VALS = `270deg 100% 80%`;
const LIGHT_PURPLE = `hsl(${LIGHT_PURPLE_VALS})`;

function EmailLayoutDemo() {
  const ref = React.useRef<HTMLDivElement>(null);
  const boundingBox = useBoundingBox(ref);

  const currentDate = new Date(new Date()).toLocaleDateString(
    'en-US',
    {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }
  );

  return (
    <Demo
      ref={ref}
      initialValues={{
        layoutType: 'sparse',
      }}
      controlPosition="top"
      outputWrapper={null}
      customGrid={{
        gridTemplateColumns: `min(${MAX_WIDTH}px, 100%)`,
        justifyContent: 'center',
      }}
      controls={[
        <DemoToggleButtonGroupControl
          id="layoutType"
          key="layoutType"
          label="Layout Type"
          inline
          options={[
            { value: 'sparse', label: 'Sparse' },
            { value: 'condensed', label: 'Condensed' },
          ]}
        />,
      ]}
    >
      {({ layoutType }) => {
        const maxContainerWidth = Math.min(
          boundingBox?.width || 10000,
          MAX_WIDTH
        );

        const width =
          layoutType === 'sparse'
            ? maxContainerWidth
            : maxContainerWidth * 0.75;

        // NOTE: This JSX is copy-pasted from /app/email/[emailSlug]/MetadataColumn.tsx. I was originally going to use a shared component, but too many little things were different (eg. the width, the <h1> markup, etc).
        return (
          <Wrapper
            layout
            data-layout-type={layoutType}
            style={{
              width,
            }}
          >
            <Title layout>
              <Motion layout>Some thoughts about passion…</Motion>
            </Title>

            <Row>
              <Label>
                <Motion layout>From</Motion>
              </Label>
              <Value>
                <Motion layout>Josh W. Comeau</Motion>
              </Value>
            </Row>
            <motion.div layout>
              <Divider layout />
            </motion.div>
            <Row>
              <Label>
                <Motion layout>Reply-To</Motion>
              </Label>
              <Value>
                <Motion layout>support@joshwcomeau.com</Motion>
              </Value>
            </Row>
            <motion.div layout>
              <Divider layout />
            </motion.div>
            <Row style={{ borderBottom: 'revert' }}>
              <Label>
                <Motion layout>Sent</Motion>
              </Label>
              <Value>
                <Motion layout>{currentDate}</Motion>
              </Value>
            </Row>
          </Wrapper>
        );
      }}
    </Demo>
  );
}

const Wrapper = styled(motion.div)`
  --color-background-values: ${DEEP_PURPLE_VALS};
  --color-background: ${DEEP_PURPLE};
  --color-light-purple-values: ${LIGHT_PURPLE_VALS};
  --color-light-purple: ${LIGHT_PURPLE};
  --shadow-color: 240deg 38% 9%;
  container-type: inline-size;
  /* Prevent layout shifts when flipping between sparse/condensed */
  min-height: 20.9375rem;
  padding: 12px;
  border: 4px solid var(--color-background);
  border-radius: 8px;
  margin-inline: auto;
  margin-top: 24px;
  color: white;
  background: var(--color-background);
  /* Stop the email address from overflowing on smaller screens */
  overflow: hidden;
`;

const Title = styled(motion.p)`
  font-size: 1.25rem;
  margin-bottom: 4px;
  text-wrap: unset;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Row = styled.dl`
  display: flex;
  align-items: baseline;
  gap: 4px 16px;
  padding-block: 16px;

  ${Wrapper}[data-layout-type='condensed'] & {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Label = styled.dt`
  min-width: 5.25rem;
  font-family: var(--font-family-mono);
  font-style: italic;
  color: var(--color-light-purple);
  font-size: 0.9375em;
  white-space: nowrap;
`;
const Value = styled.dd`
  flex: 1;
  white-space: nowrap;
`;

const Divider = styled(motion.div)`
  background: hsl(var(--color-light-purple-values) / 0.7);
  width: 100%;
  height: 2px;
  border-radius: 3px;
`;

const Motion = styled(motion.span)`
  display: inline-block;
  will-change: transform;
`;

export default EmailLayoutDemo;
